import {
  isPeriodicidadeAnual,
  isPeriodicidadeMensal,
  isPeriodicidadePeriodo,
  isPeriodicidadeSemestral,
  isPeriodicidadeTrimestral,
  Periodicidade,
  periodicidadeAnual,
  periodicidadeDiaria,
  periodicidadeMensal,
  periodicidadePeriodo,
  periodicidadeSemestral,
  periodicidadeTodoPeriodo,
  periodicidadeTrimestral
} from '../../../../shared/periodicidade';

import { ISelectOption } from '../select/models/select-option.interface';
import { DatepickerPeriodicidade, INovoDatepickerValue } from './novo-datepicker-settings.interface';

const DAY_OF_WEEK_SUNDAY = 0;
const DAY_OF_WEEK_SATURDAY = 6;

const MONTH_JANUARY = 0;
const MONTH_APRIL = 3;
const MONTH_JULY = 6;
const MONTH_OCTOBER = 9;

const FIRST_DAY_OF_MONTH = 1;
const MONTHS_PER_TRIMESTRE = 3;

const TRIMESTRE_MONTHS: Record<number, number> = {
  1: MONTH_JANUARY,
  2: MONTH_APRIL,
  3: MONTH_JULY,
  4: MONTH_OCTOBER
};

const SEMESTRE_MONTHS: Record<number, number> = {
  1: MONTH_JANUARY,
  2: MONTH_JULY
};

export const MONTHS: ReadonlyArray<ISelectOption> = [
  { id: 1, label: 'Jan' },
  { id: 2, label: 'Fev' },
  { id: 3, label: 'Mar' },
  { id: 4, label: 'Abr' },
  { id: 5, label: 'Mai' },
  { id: 6, label: 'Jun' },
  { id: 7, label: 'Jul' },
  { id: 8, label: 'Ago' },
  { id: 9, label: 'Set' },
  { id: 10, label: 'Out' },
  { id: 11, label: 'Nov' },
  { id: 12, label: 'Dez' }
];


const PERIODICIDADE_MAP: Record<DatepickerPeriodicidade, Periodicidade> = {
  diaria: periodicidadeDiaria,
  mensal: periodicidadeMensal,
  trimestral: periodicidadeTrimestral,
  semestral: periodicidadeSemestral,
  anual: periodicidadeAnual,
  periodo: periodicidadePeriodo,
  'todo-periodo': periodicidadeTodoPeriodo
};

export function getDateFromTrimestre(trimestre: number, ano: number): Date {
  const month = TRIMESTRE_MONTHS[trimestre];
  return month !== undefined ? new Date(ano, month, FIRST_DAY_OF_MONTH) : null;
}

export function getDateFromSemestre(semestre: number, ano: number): Date {
  const month = SEMESTRE_MONTHS[semestre];
  return month !== undefined ? new Date(ano, month, FIRST_DAY_OF_MONTH) : null;
}

export function buildPeriodicidadesList(
  periodicidades: DatepickerPeriodicidade[]
): Periodicidade[] {
  if (!periodicidades) {
return [];
}
  return periodicidades.map((p) => PERIODICIDADE_MAP[p]).filter(Boolean);
}

export function isDateDisabled(
  date: Date,
  datesDisabled: Date[],
  disableSunday: boolean,
  disableSaturday: boolean
): boolean {
  if (!datesDisabled || !date) {
return false;
}
  if (date.getDay() === DAY_OF_WEEK_SUNDAY && disableSunday) {
return true;
}
  if (date.getDay() === DAY_OF_WEEK_SATURDAY && disableSaturday) {
return true;
}
  return (
    datesDisabled.findIndex(
      (d) =>
        d.getDate() === date.getDate() &&
        d.getMonth() === date.getMonth() &&
        d.getFullYear() === date.getFullYear()
    ) >= 0
  );
}

export const TRIMESTRE_OPTIONS: ReadonlyArray<ISelectOption> = [
  { id: 1, label: 'Trimestre 1' },
  { id: 2, label: 'Trimestre 2' },
  { id: 3, label: 'Trimestre 3' },
  { id: 4, label: 'Trimestre 4' }
];

export const SEMESTRE_OPTIONS: ReadonlyArray<ISelectOption> = [
  { id: 1, label: 'Semestre 1' },
  { id: 2, label: 'Semestre 2' }
];

export function getTrimestreFromDate(date: Date): number {
  if (!date) {
return null;
}
  return Math.ceil((date.getMonth() + 1) / MONTHS_PER_TRIMESTRE);
}

export function getSemestreFromDate(date: Date): number {
  if (!date) {
return null;
}
  return date.getMonth() < 6 ? 1 : 2;
}

export function getDayFromDate(date: Date): number {
  if (!date) {
return null;
}
  return date.getDate();
}

export function getMonthFromDate(date: Date): number {
  if (!date) {
return null;
}
  return date.getMonth() + 1;
}

export function getYearFromDate(date: Date): number {
  if (!date) {
return null;
}
  return date.getFullYear();
}

/**
 * Formata uma Date para string local no formato YYYY-MM-DD.
 *
 * Evita a conversão UTC de toISOString(): em fusos negativos (ex: UTC-3),
 * 01/01/2026 00:00 local vira "2025-12-31T21:00:00Z", deslocando um dia.
 */
export function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseLocalDate(v: string): Date {
  return new Date(v.includes('T') ? v : v + 'T12:00:00');
}

export function extractDatepickerDate(value: INovoDatepickerValue | null | undefined): string | null {
  const date = value?.date;
  if (!date || Array.isArray(date)) {
    return null;
  }
  return date;
}

export function extractDatepickerRange(
  value: INovoDatepickerValue | null | undefined,
): { inicio: string | null; fim: string | null } {
  const date = value?.date;
  if (!date) {
    return { inicio: null, fim: null };
  }
  if (Array.isArray(date)) {
    const [inicio = null, fim = null] = date;
    return { inicio: inicio || null, fim: fim || null };
  }
  return computeRangeFromPeriodicidade(date, value?.periodicidade);
}

function computeRangeFromPeriodicidade(
  date: string,
  periodicidade: number | null | undefined,
): { inicio: string; fim: string } {
  const d = parseLocalDate(date);
  const year = d.getFullYear();
  const month = d.getMonth();

  if (isPeriodicidadeMensal(periodicidade)) {
    const inicio = new Date(year, month, 1);
    const fim = new Date(year, month + 1, 0);
    return { inicio: toLocalDateString(inicio), fim: toLocalDateString(fim) };
  }
  if (isPeriodicidadeTrimestral(periodicidade)) {
    const inicio = new Date(year, month, 1);
    const fim = new Date(year, month + 3, 0);
    return { inicio: toLocalDateString(inicio), fim: toLocalDateString(fim) };
  }
  if (isPeriodicidadeSemestral(periodicidade)) {
    const inicio = new Date(year, month, 1);
    const fim = new Date(year, month + 6, 0);
    return { inicio: toLocalDateString(inicio), fim: toLocalDateString(fim) };
  }
  if (isPeriodicidadeAnual(periodicidade)) {
    const inicio = new Date(year, 0, 1);
    const fim = new Date(year, 12, 0);
    return { inicio: toLocalDateString(inicio), fim: toLocalDateString(fim) };
  }
  return { inicio: date, fim: date };
}

export function wrapDatepickerDate(
  iso: string | null | undefined,
  periodicidade: number = periodicidadeDiaria.id,
): INovoDatepickerValue | null {
  if (!iso) {
    return null;
  }
  return { date: iso, periodicidade };
}

export function wrapDatepickerRange(
  inicio: string | null | undefined,
  fim: string | null | undefined,
  periodicidade: number = periodicidadePeriodo.id,
): INovoDatepickerValue | null {
  const dates = [inicio, fim].filter(Boolean) as string[];
  if (dates.length === 0) {
    return null;
  }
  return { date: dates, periodicidade };
}

/**
 * Reconstrói um INovoDatepickerValue a partir de datas vindas da URL
 * (inicio/fim) e da periodicidade obtida de uma fonte externa
 * (sessionStorage, memória do componente etc).
 *
 * A periodicidade é estado de UI e não vai pra URL: o consumidor é responsável
 * por persisti-la onde fizer sentido para o caso de uso.
 *
 * Para periodicidades de bucket (diária, mensal, trimestral, semestral, anual)
 * o datepicker emite uma data canônica (primeiro dia do bucket), então `inicio`
 * é a âncora obrigatória — `fim` é ignorado. Para Período, mantemos o range
 * explícito. Sem periodicidade conhecida (URL legada / storage zerado)
 * aplicamos uma heurística: duas datas distintas viram range, uma data só
 * vira single-date.
 *
 * Retorna `null` quando não há nenhuma data utilizável, ou quando a
 * periodicidade exige `inicio` e ele está ausente.
 */
export function rebuildDatepickerValueFromUrl(
  inicio: string | null | undefined,
  fim: string | null | undefined,
  periodicidade: number | null | undefined,
): INovoDatepickerValue | null {
  if (!inicio && !fim) {
    return null;
  }
  if (periodicidade && isPeriodicidadePeriodo(periodicidade)) {
    return wrapDatepickerRange(inicio, fim, periodicidade);
  }
  if (periodicidade) {
    if (!inicio) {
      return null;
    }
    return wrapDatepickerDate(inicio, periodicidade);
  }
  if (fim && inicio && fim !== inicio) {
    return wrapDatepickerRange(inicio, fim);
  }
  return wrapDatepickerDate(inicio ?? fim ?? null);
}
